import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { useAuth } from '@/hooks/useAuth';
import AppLayout from '@/components/Layout/AppLayout';
import ColorModeMenuButton from '@/components/ColorModeMenuButton';
import { APP_TITLE } from '@/constants/branding';
import { useAppLogo } from '@/contexts/LogoProvider';
import { loginLogoImageSx, loginLogoTraySx } from '@/utils/loginShellLogo';
import { MAIN_APP_LOGIN_URL } from '@/types';
import { useColorMode } from '@/contexts/ColorModeContext';
import { glassSurface } from '@/utils/glassTokens';

export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const bareDisplay = /^\/scoreboard\/[^/]+\/display$/.test(location.pathname);
  if (bareDisplay) return <Outlet />;

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

export function LoginRedirect() {
  const { user, loading } = useAuth();
  const { resolvedMode } = useColorMode();
  const logoSrc = useAppLogo();
  const isDark = resolvedMode === 'dark';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        p: 3,
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ColorModeMenuButton />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
          ...glassSurface(isDark),
          bgcolor: isDark ? 'rgba(21, 27, 34, 0.85)' : 'background.paper',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            overflow: 'hidden',
            mx: 'auto',
            mb: 2,
            ...loginLogoTraySx(isDark),
          }}
        >
          <Box component="img" src={logoSrc} alt="" sx={loginLogoImageSx()} />
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {APP_TITLE}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          請先登入香港手球裁判會主系統，然後從側邊選單進入「影片分析」。
        </Typography>
        <Button
          component="a"
          href={MAIN_APP_LOGIN_URL}
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<LoginIcon />}
          fullWidth
        >
          前往主系統登入
        </Button>
      </Paper>
    </Box>
  );
}
